import React, { useEffect, useState } from 'react'
import {
    Box,
    Button,
    Flex,
    Text,
    Table,
    Thead,
    Tbody,
    Tr,
    Th,
    Td,
    Input,
    Modal,
    ModalOverlay,
    ModalContent,
    ModalHeader,
    ModalBody,
    ModalCloseButton,
    ModalFooter,
    FormControl,
    FormLabel,
    useDisclosure,
    useToast,
    IconButton,
    Checkbox,
    CheckboxGroup
} from '@chakra-ui/react'
import { DeleteIcon, EditIcon } from '@chakra-ui/icons'
import Card from 'components/card/Card'
import axiosInstance from 'api/axiosInstance'
import { useTranslation } from 'react-i18next'

const EmployeesModule = () => {
    const { t } = useTranslation()
    const tr = (key, defaultValue) => t(key, { defaultValue })
    const [employees, setEmployees] = useState([])
    const [allPermissions, setAllPermissions] = useState([])
    const [name, setName] = useState('')
    const [email, setEmail] = useState('')
    const [password, setPassword] = useState('')
    const [selectedPermissions, setSelectedPermissions] = useState([])
    const [isEditMode, setIsEditMode] = useState(false)
    const [selectedEmployee, setSelectedEmployee] = useState(null)

    const { isOpen, onOpen, onClose } = useDisclosure()
    const toast = useToast()

    useEffect(() => {
        fetchEmployees()
        fetchPermissions()
    }, [])

    const fetchEmployees = async () => {
        try {
            const res = await axiosInstance.get('/employees')
            setEmployees(res.data)
        } catch (err) {
            toast({ title: tr('team.toast.failedFetchEmployees', 'Failed to fetch employees'), status: 'error', isClosable: true })
        }
    }

    const fetchPermissions = async () => {
        try {
            const res = await axiosInstance.get('/permissions')
            setAllPermissions(res.data)
        } catch (err) {
            toast({ title: tr('team.toast.failedFetchPermissions', 'Failed to fetch permissions'), status: 'error', isClosable: true })
        }
    }

    const resetForm = () => {
        setName('')
        setEmail('')
        setPassword('')
        setSelectedPermissions([])
        setSelectedEmployee(null)
        setIsEditMode(false)
    }

    const openCreate = () => {
        resetForm()
        onOpen()
    }

    const openEdit = employee => {
        setSelectedEmployee(employee)
        setName(employee.name)
        setEmail(employee.email)
        setPassword('')
        setSelectedPermissions(employee.permissions ? employee.permissions.map(p => p.slug) : [])
        setIsEditMode(true)
        onOpen()
    }

    const handleSave = async () => {
        try {
            if (isEditMode && selectedEmployee) {
                await axiosInstance.post(`/employees/${selectedEmployee.id}`, {
                    name,
                    email,
                    permissions: selectedPermissions,
                    ...(password && { password })
                })
                toast({ title: tr('team.toast.employeeUpdated', 'Employee updated'), status: 'success', isClosable: true })
            } else {
                await axiosInstance.post('/employees', { name, email, password, permissions: selectedPermissions })
                toast({ title: tr('team.toast.employeeCreated', 'Employee created'), status: 'success', isClosable: true })
            }
            fetchEmployees()
            onClose()
        } catch (err) {
            toast({
                title: tr('team.toast.saveFailed', 'Save failed'),
                description: err?.response?.data?.message || tr('team.toast.somethingWrong', 'Something went wrong'),
                status: 'error',
                isClosable: true
            })
        }
    }

    const handleDelete = async id => {
        if (!window.confirm(tr('team.confirm.deleteEmployee', 'Are you sure you want to delete this employee?'))) return
        try {
            await axiosInstance.delete(`/employees/${id}`)
            setEmployees(prev => prev.filter(e => e.id !== id))
            toast({ title: tr('team.toast.employeeDeleted', 'Employee deleted'), status: 'info', isClosable: true })
        } catch (err) {
            toast({ title: tr('team.toast.deleteFailed', 'Delete failed'), status: 'error', isClosable: true })
        }
    }

    return (
        <Box pt={{ base: '130px', md: '80px', xl: '80px' }} maxW="xxl" px={{ base: '20px', md: '40px' }}>
            <Flex justifyContent="space-between" alignItems="center" mb="20px">
                <Text fontSize="2xl" fontWeight="bold">
                </Text>
                <Button colorScheme="blue" onClick={openCreate}>
                    {tr('team.createEmployee', 'Create Employee')}
                </Button>
            </Flex>

            <Card>
                <Box overflowX="auto" p={4}>
                    <Table variant="simple" size="sm">
                        <Thead>
                            <Tr>
                                <Th>ID</Th>
                                <Th>{tr('team.table.name', 'Name')}</Th>
                                <Th>{tr('team.table.email', 'Email')}</Th>
                                <Th>{tr('team.table.permissions', 'Permissions')}</Th>
                                <Th>{tr('team.table.actions', 'Actions')}</Th>
                            </Tr>
                        </Thead>
                        <Tbody>
                            {employees.map(emp => (
                                <Tr key={emp.id}>
                                    <Td>{emp.id}</Td>
                                    <Td>{emp.name}</Td>
                                    <Td maxW="100">{emp.email}</Td>
                                    <Td maxW="100">{emp.permissions && emp.permissions.map(p => p.name).join(', ')}</Td>
                                    <Td>
                                        <Flex gap={2}>
                                            <IconButton icon={<EditIcon />} aria-label={tr('team.aria.editMember', 'edit member')} onClick={() => openEdit(emp)} />
                                            <IconButton icon={<DeleteIcon />} aria-label={tr('team.aria.deleteEmployee', 'delete employee')} onClick={() => handleDelete(emp.id)} />
                                        </Flex>
                                    </Td>
                                </Tr>
                            ))}
                        </Tbody>
                    </Table>
                </Box>
            </Card>

            <Modal isOpen={isOpen} onClose={onClose} size="lg">
                <ModalOverlay />
                <ModalContent>
                    <ModalHeader>{isEditMode ? tr('team.editEmployee', 'Edit Employee') : tr('team.createEmployee', 'Create Employee')}</ModalHeader>
                    <ModalCloseButton />
                    <ModalBody>
                        <FormControl mb={4}>
                            <FormLabel>{tr('team.form.name', 'Name')}</FormLabel>
                            <Input value={name} onChange={e => setName(e.target.value)} />
                        </FormControl>
                        <FormControl mb={4}>
                            <FormLabel>{tr('team.form.email', 'Email')}</FormLabel>
                            <Input value={email} onChange={e => setEmail(e.target.value)} />
                        </FormControl>
                        <FormControl mb={4}>
                            <FormLabel>{isEditMode ? tr('team.form.newPasswordOptional', 'New Password (optional)') : tr('team.form.password', 'Password')}</FormLabel>
                            <Input type="password" value={password} onChange={e => setPassword(e.target.value)} />
                        </FormControl>
                        <FormControl mb={4}>
                            <FormLabel>{tr('team.form.permissions', 'Permissions')}</FormLabel>
                            <CheckboxGroup value={selectedPermissions} onChange={setSelectedPermissions}>
                                <Flex direction="column" gap={2}>
                                    {allPermissions.map(permission => (
                                        <Checkbox key={permission.id} value={permission.slug}>
                                            {permission.name}
                                        </Checkbox>
                                    ))}
                                </Flex>
                            </CheckboxGroup>
                        </FormControl>
                    </ModalBody>
                    <ModalFooter>
                        <Button variant="ghost" onClick={onClose}>
                            {tr('team.cancel', 'Cancel')}
                        </Button>
                        <Button colorScheme="blue" ml={3} onClick={handleSave}>
                            {isEditMode ? tr('team.update', 'Update') : tr('team.save', 'Save')}
                        </Button>
                    </ModalFooter>
                </ModalContent>
            </Modal>
        </Box>
    )
}

export default EmployeesModule